import { makeAutoObservable, runInAction } from "mobx";
import { db } from "@stores/environments/Database.web";
import { ref, get, set, child } from "firebase/database";
import { IDesenvolvimentoRange } from "@interfaces/idesenvolvimentoRange";
import { Language } from "@constants/language";
import { logService } from "../services/LogService";

class DesenvolvimentoStore {
  ranges: IDesenvolvimentoRange[] = [];
  loading: boolean = false;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  fetchRanges = async () => {
    this.loading = true;
    const dbRef = ref(db);

    try {
      const snapshot = await get(child(dbRef, "desenvolver"));
      if (snapshot.exists()) {
        const data = snapshot.val();

        const allRanges: IDesenvolvimentoRange[] = [];

        for (const id of Object.keys(data)) {
          const idiomas = data[id];
          for (const langKey of Object.keys(idiomas)) {
            allRanges.push({
              id: langKey,
              lang: id as Language,
              ...idiomas[langKey],
            });
          }
        }

        runInAction(() => {
          this.ranges = allRanges.filter(range => !range.deleted);
          this.loading = false;
        });
      } else {
        runInAction(() => {
          this.ranges = [];
          this.loading = false;
        });
      }
    } catch (error) {
      console.error("Erro ao buscar ranges de desenvolvimento:", error);
      runInAction(() => {
        this.error = "Erro ao carregar os desenvolvimentos.";
        this.loading = false;
      });
    }
  };

  saveRange = async (range: Omit<IDesenvolvimentoRange, "id">, id: string) => {
    if (!id) {
      console.error("ID (range) é obrigatório para salvar o desenvolvimento.");
      return;
    }
    
    // Garantir que ambos os campos active sejam sempre definidos
    const rangeToSave = {
      ...range,
      active: range.active !== false, // Default para true se não definido
      intro: {
        ...range.intro,
        active: range.intro?.active !== false // Default para true se não definido
      }
    };
    
    const dbRef = ref(db, `desenvolver/${range.lang}/${id}`);
    
    // Busca o valor anterior para verificar se é update ou create
    let oldValue = null;
    try {
      const snapshot = await get(dbRef);
      oldValue = snapshot.exists() ? snapshot.val() : null;
    } catch (error) {
      console.error('Erro ao buscar valor anterior:', error);
    }
    
    const isUpdate = !!oldValue;
    
    await set(dbRef, rangeToSave);
    
    // Registra o log
    const logData = isUpdate 
      ? logService.updateLogData(
          'DesenvolvimentoRange',
          id,
          `desenvolver/${range.lang}/${id}`,
          oldValue,
          rangeToSave,
          `Range de desenvolvimento ${isUpdate ? 'atualizado' : 'criado'}: ${range.title} (${range.lang})`
        )
      : logService.createLogData(
          'DesenvolvimentoRange',
          id,
          `desenvolver/${range.lang}/${id}`,
          rangeToSave,
          `Range de desenvolvimento criado: ${range.title} (${range.lang})`
        );
    
    await logService.logOperation(logData);
    
    this.fetchRanges();
  };

  deleteRange = async (id: string, lang: string) => {
    const confirmed = window.confirm("Tem certeza que deseja excluir este range?");
    if (!confirmed) return;
    
    // Busca o valor anterior para o log
    const dbRef = ref(db, `desenvolver/${lang}/${id}`);
    let oldValue = null;
    try {
      const snapshot = await get(dbRef);
      oldValue = snapshot.exists() ? snapshot.val() : null;
    } catch (error) {
      console.error('Erro ao buscar valor anterior:', error);
    }
    
    const deleteRef = ref(db, `desenvolver/${lang}/${id}/deleted`);
    await set(deleteRef, true);
    
    // Registra o log
    if (oldValue) {
      const logData = logService.deleteLogData(
        'DesenvolvimentoRange',
        id,
        `desenvolver/${lang}/${id}`,
        oldValue,
        `Range de desenvolvimento excluído: ${oldValue.title} (${lang})`
      );
      
      await logService.logOperation(logData);
    }
    
    this.fetchRanges();
  };
}

export const desenvolverStore = new DesenvolvimentoStore();
